/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Form } from 'react-final-form';
import { Config } from 'final-form';
import { StyleSheet, css } from 'aphrodite';
import { ActionButton, DomEventListener } from '@blend/enterprise-components';
import { keyCodes } from '@blend/shared-react';

import { useQuery, useSSOHooks } from 'id-utils/login';
import { useCancelHook } from 'id-utils/login';

import { FormCardWrapper } from './FormCardWrapper';
import { FormCardContent } from './FormCardContent';

const styles = StyleSheet.create({
  buttonWrapper: {
    ':not(:last-child)': {
      marginRight: 12,
    },
  },
  escapeContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
});

export namespace FormCard {
  export interface Props<FormValues> {
    additionalFooterComponent?: JSX.Element;
    headerLabel?: JSX.Element;
    headerTitle: string;
    id: string;
    onSubmit: Config<FormValues, unknown>['onSubmit'];
    getCancelHandler?: typeof useCancelHook;
    getSSOHandlers?: typeof useSSOHooks;
    showCancel?: boolean;
    showSSOButton?: boolean;
    showBlendSSOButton?: boolean;
    cancelLabel?: string;
    SSOLabel?: string;
    submitLabel: string;
    errorMessage?: string;
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const FormCard = <FormValues,>({
  onSubmit,
  children,
  headerTitle,
  id,
  submitLabel,
  headerLabel,
  showCancel = true,
  showSSOButton = false,
  showBlendSSOButton = false,
  getCancelHandler = useCancelHook,
  getSSOHandlers = useSSOHooks,
  cancelLabel = 'Cancel',
  SSOLabel = 'Continue with your Account',
  errorMessage = '',
  additionalFooterComponent,
}: React.PropsWithChildren<FormCard.Props<FormValues>>) => {
  const [customError, setCustomError] = React.useState('');
  const { error } = useQuery<{ error?: string }>();
  const cancelHandler = getCancelHandler(setCustomError);
  const SSOHandlers = getSSOHandlers();
  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, submitError, submitting }) => {
        const displayError = submitError || error || errorMessage || customError;
        return (
          <form id={id} onSubmit={handleSubmit}>
            <FormCardWrapper>
              <FormCardContent
                displayError={displayError}
                formId={id}
                headerLabel={headerLabel}
                headerTitle={headerTitle}
                children={children}
              />
              {showSSOButton && (
                <div className={css(styles.footerBar)}>
                  <div className={css(styles.buttonWrapper)}>
                    <ActionButton
                      disabled={submitting}
                      id={`${id}-sso`}
                      label={SSOLabel}
                      onClick={SSOHandlers.TPR}
                      theme="tertiary"
                    />
                  </div>
                </div>
              )}
              {showBlendSSOButton && (
                <div className={css(styles.footerBar)}>
                  <div className={css(styles.buttonWrapper)}>
                    <ActionButton
                      disabled={submitting}
                      id={`${id}-blend-sso`}
                      label="Continue with Blend SSO"
                      onClick={SSOHandlers.BLEND}
                      theme="tertiary"
                    />
                  </div>
                </div>
              )}
              <div className={css(styles.footerBar)}>
                {additionalFooterComponent}
                {showCancel && (
                  <div className={css(styles.buttonWrapper)}>
                    <ActionButton
                      disabled={submitting}
                      id={`${id}-exit`}
                      label={cancelLabel}
                      onClick={cancelHandler}
                      theme="tertiary"
                    />
                  </div>
                )}
                <ActionButton id={`${id}-submit`} isLoading={submitting} label={submitLabel} onClick={handleSubmit} />
              </div>
            </FormCardWrapper>
            <DomEventListener
              cb={(event: React.KeyboardEvent<HTMLInputElement>) => {
                // note: editor is warning about usage of keyCode saying that it is deprecated, not supported
                if (event.keyCode === keyCodes.ENTER) {
                  void handleSubmit(event);
                }
              }}
              eventName="keydown"
              target={window}
            />
          </form>
        );
      }}
    </Form>
  );
};
